import React, { useMemo, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ScatterChart, Scatter, ZAxis, Cell, LabelList,
} from "recharts";
import { T, SERIES, SERIF, GOLD_INK } from "../theme/tokens.js";
import { Panel, Empty, Btn, Kpi, Chip } from "../components/ui/index.jsx";
import DrillPanel from "../components/DrillPanel.jsx";
import SiteBoard from "../components/SiteBoard.jsx";
import { useRegisters } from "../lib/useRegister.js";
import {
  vendorRollup, riskScore, isOpenRisk, isScored, isRatedHigh, highRiskThreshold,
  isComplete, isOverdue, effectiveDueDate, invoiceSummary, AGING_BUCKETS,
  DELIVERABLE_STATES, groupByState,
} from "../lib/calc.js";
import { fmtK, fmtMoney, fmtDate } from "../lib/format.js";

/* Every chart can show its own numbers. A chart is a summary; the numbers are
   the thing itself, and anyone who cannot read the colours still needs them. */
function ChartPanel({ title, accent, children, table, note, right }) {
  const [showTable, setShowTable] = useState(false);
  return (
    <Panel title={title} accent={accent} right={
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {right}
        {table && (
          <Btn small kind="ghost" onClick={() => setShowTable(!showTable)}>
            {showTable ? "Show chart" : "Show numbers"}
          </Btn>
        )}
      </div>
    }>
      {showTable && table ? table : children}
      {note && <div style={{ fontSize: 12, color: T.soft, marginTop: 8 }}>{note}</div>}
    </Panel>
  );
}

/* What a click on a mark actually hands back.
 *
 * Recharts gives a bar its datum with the fields spread onto the argument and
 * gives a scatter point its shape props with the datum tucked under `payload`.
 * Reading the wrong one is silent: the handler runs, finds no rows, and opens
 * nothing, which reads as a broken click. One helper, used by every chart
 * here, so a click means the same thing whatever drew the mark. */
const datum = (d) => (d && typeof d === "object" ? (d.payload ?? d) : {});

const axis = { tick: { fontSize: 11, fill: T.soft }, stroke: T.line };
const gridProps = { strokeDasharray: "3 3", stroke: T.line, vertical: false };

function tooltipStyle() {
  return {
    contentStyle: {
      background: "#fff", border: `1px solid ${T.line}`, borderRadius: 4,
      fontSize: 12.5, color: T.ink, boxShadow: "0 4px 14px rgba(20,40,75,0.14)",
    },
    labelStyle: { color: T.navy, fontWeight: 700 },
  };
}

function SimpleTable({ head, rows, onRow }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead><tr>{head.map((h, i) => (
          <th key={h} style={{
            textAlign: i ? "right" : "left", padding: "6px 9px",
            borderBottom: `2px solid ${T.navy}`, color: T.navy, fontSize: 11,
            fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em",
          }}>{h}</th>
        ))}</tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={i} onClick={onRow ? () => onRow(i) : undefined}
            style={{ borderBottom: `1px solid ${T.line}`,
                     cursor: onRow ? "pointer" : "default" }}>
            {r.map((c, j) => (
              <td key={j} style={{ padding: "6px 9px", textAlign: j ? "right" : "left" }}>{c}</td>
            ))}
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

export default function Dashboards({ contract, onReloadContracts }) {
  const { data } = useRegisters(
    ["deliverables", "risks", "permits", "invoices", "vendors", "expenses", "resources"],
    contract?.id,
  );
  return <DashboardsView contract={contract} data={data} onReloadContracts={onReloadContracts} />;
}


/* The view, separated from the fetch so the charts can be rendered against
   known numbers and looked at. Charts are the one thing a build passing tells
   you nothing about. */
export function DashboardsView({ contract, data, onReloadContracts }) {
  const [drill, setDrill] = useState(null);
  const open = (kind, title, rows) => setDrill(rows?.length ? { kind, title, rows } : null);
  const model = useMemo(() => {
    if (!data) return null;
    const d = data.deliverables;
    const rollup = vendorRollup(d);

    /* Overdue is drawn instead of, not as well as, the state underneath it, so
       the four add up to the total. The rule lives in calc so this chart, the
       vendor chart and the report cannot drift apart. */
    const statusRows = groupByState(d);
    const status = Object.fromEntries(
      Object.entries(statusRows).map(([k, v]) => [k, v.length]));

    /* Every point on every chart carries the rows it was counted from, so a
       click can open them. A number nobody can open is a number nobody can
       act on. */
    const byStage = {};
    for (const row of d) {
      const k = row.stage || "Unassigned";
      byStage[k] = byStage[k] ?? { stage: k, Complete: 0, Outstanding: 0, rows: { Complete: [], Outstanding: [] } };
      if (isComplete(row)) { byStage[k].Complete += 1; byStage[k].rows.Complete.push(row); }
      else { byStage[k].Outstanding += 1; byStage[k].rows.Outstanding.push(row); }
    }

    const vendors = rollup.rows
      .filter((r) => r.total >= 1)
      .sort((a, b) => b.total - a.total)
      .map((r) => {
        const mine = r.records.total;
        const rows = groupByState(mine);
        /* The counts are the lengths of the rows they open. There is no way
           for the bar to say twelve and the click to find none. */
        return {
          vendor: r.vendor.length > 18 ? r.vendor.slice(0, 18) + "…" : r.vendor,
          full: r.vendor,
          ...Object.fromEntries(DELIVERABLE_STATES.map((k) => [k, rows[k].length])),
          overdueCD: r.overdueContractDeliverables,
          percent: r.percentComplete,
          rows,
        };
      });

    /* One dot per occupied cell of the matrix, sized by how many risks sit
       there, so 74 risks at 3x3 read as one large dot rather than 74 stacked
       invisibly on the same point. */
    const threshold = highRiskThreshold(contract);
    const cells = {};
    /* Only scored risks belong on a matrix. A RAID log that rates a risk High
       and leaves the numbers blank has no coordinates, and putting it at one
       by one would draw the program's worst risks in the safest corner.
       Those are counted underneath the chart instead. */
    const openRisks = data.risks.filter(isOpenRisk);
    for (const r of openRisks.filter(isScored)) {
      const l = Number(r.likelihood);
      const i = Number(r.impact);
      const k = `${l}:${i}`;
      cells[k] = cells[k] ?? { likelihood: l, impact: i, count: 0, score: l * i, titles: [], rows: [] };
      cells[k].count += 1;
      cells[k].rows.push(r);
      if (cells[k].titles.length < 4) cells[k].titles.push(r.title);
    }
    const matrix = Object.values(cells);
    const unscored = openRisks.filter((r) => !isScored(r));
    const unscoredHigh = unscored.filter(isRatedHigh).length;

    const permitsByCategory = {};
    for (const p of data.permits) {
      const k = (p.category || "Uncategorised").slice(0, 26);
      permitsByCategory[k] = permitsByCategory[k]
        ?? { category: k, Closed: 0, Open: 0, rows: { Closed: [], Open: [] } };
      if (["Closed", "Completed", "Approved", "N/A"].includes(p.status)) {
        permitsByCategory[k].Closed += 1; permitsByCategory[k].rows.Closed.push(p);
      } else {
        permitsByCategory[k].Open += 1; permitsByCategory[k].rows.Open.push(p);
      }
    }

    const invoices = invoiceSummary(data.invoices, {
      vendors: data.vendors, contractTerms: contract?.payment_terms_days,
    });

    return {
      total: d.length, status, statusRows, rollup,
      deliverables: d,
      allRisks: data.risks, openRiskRows: openRisks,
      unscoredRows: unscored,
      stages: Object.values(byStage).sort((a, b) =>
        (b.Complete + b.Outstanding) - (a.Complete + a.Outstanding)),
      stageMax: Math.max(1, ...Object.values(byStage).map((x) => x.Complete + x.Outstanding)),
      vendors, matrix, threshold,
      unscored: unscored.length, unscoredHigh,
      vendorMax: Math.max(1, ...vendors.map((v) =>
        v.Complete + v["In progress"] + v["Not started"] + v.Overdue)),
      permits: Object.values(permitsByCategory)
        .sort((a, b) => (b.Open + b.Closed) - (a.Open + a.Closed)).slice(0, 12),
      permitMax: Math.max(1, ...Object.values(permitsByCategory).map((x) => x.Open + x.Closed)),
      aging: AGING_BUCKETS.map((b) => ({
        bucket: b, dollars: invoices.buckets[b].dollars, count: invoices.buckets[b].count,
        rows: invoices.open.filter((p) => (p.aging.bucket || "Within terms") === b)
          .map((p) => ({ ...p.invoice, _aging: p.aging })),
      })),
      invoices,
      openRisks: data.risks.filter(isOpenRisk).length,
      highRisks: data.risks.filter((r) => isOpenRisk(r) && riskScore(r) >= threshold).length,
    };
  }, [data, contract]);

  if (!model) return <Empty text="Loading..." />;
  if (model.total === 0 && model.openRisks === 0 && model.permits.length === 0) {
    return (
      <Panel title="Nothing to chart yet">
        <Empty text="Add deliverables, risks or permits and this fills in on its own." />
      </Panel>
    );
  }

  const statusOrder = DELIVERABLE_STATES;
  const statusFill = {
    Complete: SERIES.complete, "In progress": SERIES.inProgress,
    "Not started": SERIES.notStarted, Overdue: SERIES.overdue,
  };

  /* The KPI cards open too. They are the numbers people quote, so they are the
     ones most often asked to be justified. */
  const clickable = { cursor: "pointer" };

  return (
    <div style={{ display: "grid", gap: 16, gridTemplateColumns: "minmax(0, 1fr)" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
        <div style={clickable} onClick={() => open("deliverables", "All deliverables", model.deliverables)}>
          <Kpi label="Deliverables" value={model.total}
            sub={model.rollup.total.percentComplete !== null
              ? `${model.rollup.total.percentComplete}% complete` : ""} />
        </div>
        <div style={clickable} onClick={() => open("deliverables", "Overdue deliverables", model.statusRows.Overdue)}>
          <Kpi label="Overdue" value={model.status.Overdue}
            sub={`${model.rollup.total.overdueContractDeliverables} are contract deliverables`}
            color={model.status.Overdue ? T.risk : T.navy} />
        </div>
        <div style={clickable} onClick={() => open("risks", "Open risks", model.openRiskRows)}>
          <Kpi label="Open risks" value={model.openRisks}
            sub={`${model.highRisks} at or above ${model.threshold}`}
            color={model.highRisks ? T.risk : T.navy} />
        </div>
        <div style={clickable} onClick={() => open("permits", "All permits", data.permits)}>
          <Kpi label="Permits" value={model.permits.reduce((s, p) => s + p.Open + p.Closed, 0)}
            sub={`${model.permits.reduce((s, p) => s + p.Open, 0)} still open`} />
        </div>
      </div>

      <DrillPanel drill={drill} onClose={() => setDrill(null)} />

      <div style={{ fontSize: 12.5, color: T.soft }}>
        Every card, bar, dot and row on this page opens the records behind it.
        Click one.
      </div>

      {/* Above the charts on purpose. On a program run site by site this is
          the first question anybody asks, and the charts underneath answer the
          second one. */}
      <SiteBoard
        contract={contract}
        permits={data.permits}
        risks={data.risks}
        siteLabel={contract?.site_label || "Site"}
        onOpen={(kind, title, rows) => open(kind, title, rows)}
        onSaved={onReloadContracts}
      />

      {model.total > 0 && (
        <ChartPanel
          title="Deliverables by state"
          note="Overdue replaces the underlying state rather than sitting alongside it, so the four add up to the total."
          table={<SimpleTable head={["State", "Count", "Share"]}
            onRow={(i) => open("deliverables", `${statusOrder[i]} deliverables`, model.statusRows[statusOrder[i]])}
            rows={statusOrder.map((k) => [k, model.status[k],
              `${Math.round((model.status[k] / model.total) * 100)}%`])} />}
        >
          <ResponsiveContainer width="100%" height={120}>
            <BarChart layout="vertical" data={[{ name: "All", ...model.status }]}
              margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
              <XAxis type="number" hide domain={[0, model.total]} />
              <YAxis type="category" dataKey="name" hide />
              <Tooltip {...tooltipStyle()} />
              <Legend wrapperStyle={{ fontSize: 12, color: T.soft }} />
              {statusOrder.map((k, i) => (
                <Bar key={k} dataKey={k} stackId="s" fill={statusFill[k]}
                  cursor="pointer"
                  onClick={() => open("deliverables", `${k} deliverables`, model.statusRows[k])}
                  radius={i === 0 ? [4, 0, 0, 4] : i === statusOrder.length - 1 ? [0, 4, 4, 0] : 0}>
                  {/* A value label sits on top of the mark it belongs to, and an SVG <text> is a
                      click target like anything else, so without this the label swallows the
                      click and the bar underneath never opens. Nothing here is meant to be
                      clicked except the mark itself. */}
                  <LabelList dataKey={k} position="center"
                    style={{ fill: "#fff", fontSize: 12, fontWeight: 700, pointerEvents: "none" }}
                    formatter={(v) => (v > model.total * 0.05 ? v : "")} />
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>
      )}

      {model.vendors.length > 0 && (
        <ChartPanel
          title="Deliverables by vendor"
          accent={T.gold}
          note="Sorted by how many each vendor carries. Overdue is shown separately from the state it would otherwise sit in."
          table={<SimpleTable head={["Vendor", "Total", "Complete", "In progress", "Overdue", "Overdue CD", "%"]}
            onRow={(i) => open("deliverables", `${model.vendors[i].full}: all`,
              Object.values(model.vendors[i].rows).flat())}
            rows={model.vendors.map((v) => [v.full, v.Complete + v["In progress"] + v["Not started"] + v.Overdue,
              v.Complete, v["In progress"], v.Overdue, v.overdueCD, v.percent === null ? "-" : `${v.percent}%`])} />}
        >
          <ResponsiveContainer width="100%" height={Math.max(200, model.vendors.length * 42)}>
            <BarChart layout="vertical" data={model.vendors} margin={{ left: 8, right: 24 }}>
              <CartesianGrid {...gridProps} vertical horizontal={false} />
              <XAxis type="number" allowDecimals={false} {...axis} />
              <YAxis type="category" dataKey="vendor" width={140} {...axis} />
              <Tooltip {...tooltipStyle()} />
              <Legend wrapperStyle={{ fontSize: 12, color: T.soft }} />
              {statusOrder.map((k, i) => (
                <Bar key={k} dataKey={k} stackId="v" fill={statusFill[k]}
                  cursor="pointer"
                  onClick={(d) => { const dat = datum(d);
                    open("deliverables", `${dat.full}: ${k.toLowerCase()}`, dat.rows?.[k]); }}
                  radius={i === statusOrder.length - 1 ? [0, 4, 4, 0] : 0} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>
      )}

      {model.stages.length > 0 && (
        <ChartPanel
          title="Progress by stage"
          table={<SimpleTable head={["Stage", "Complete", "Outstanding"]}
            onRow={(i) => open("deliverables", `${model.stages[i].stage}: all`,
              [...model.stages[i].rows.Complete, ...model.stages[i].rows.Outstanding])}
            rows={model.stages.map((s) => [s.stage, s.Complete, s.Outstanding])} />}
        >
          <ResponsiveContainer width="100%" height={Math.max(200, model.stages.length * 38)}>
            <BarChart layout="vertical" data={model.stages} margin={{ left: 8, right: 24 }}>
              <CartesianGrid {...gridProps} vertical horizontal={false} />
              <XAxis type="number" allowDecimals={false} {...axis} />
              <YAxis type="category" dataKey="stage" width={190} {...axis} />
              <Tooltip {...tooltipStyle()} />
              <Legend wrapperStyle={{ fontSize: 12, color: T.soft }} />
              <Bar dataKey="Complete" stackId="a" fill={SERIES.complete} cursor="pointer"
                onClick={(d) => { const dat = datum(d);
                  open("deliverables", `${dat.stage}: complete`, dat.rows?.Complete); }} />
              <Bar dataKey="Outstanding" stackId="a" fill={SERIES.inProgress} radius={[0, 4, 4, 0]}
                cursor="pointer"
                onClick={(d) => { const dat = datum(d);
                  open("deliverables", `${dat.stage}: outstanding`, dat.rows?.Outstanding); }} />
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>
      )}

      {(model.matrix.length > 0 || model.unscored > 0) && (
        <ChartPanel
          title="Open risk matrix"
          accent={T.gold}
          right={<Chip color={GOLD_INK}>High at {model.threshold}</Chip>}
          note={`Each dot is one cell of the matrix, sized by how many risks sit there. Red is at or above this contract's line of ${model.threshold}. That line belongs to the contract: a register scoring one to three cannot reach fifteen.`
            + (model.unscored
                ? ` ${model.unscored} open risk${model.unscored === 1 ? " is" : "s are"} rated rather than scored and so cannot be plotted here, ${model.unscoredHigh} of them rated High.`
                : "")}
          table={model.matrix.length > 0
            ? (() => {
                const sorted = [...model.matrix].sort((a, b) => b.score - a.score);
                return <SimpleTable head={["Likelihood", "Impact", "Score", "Risks"]}
                  onRow={(i) => open("risks", `Open risks scoring ${sorted[i].score}`, sorted[i].rows)}
                  rows={sorted.map((c) => [c.likelihood, c.impact, c.score, c.count])} />;
              })()
            : null}
        >
          {model.matrix.length === 0 ? (
            <Empty>
              No open risk on this contract carries a likelihood and an impact,
              so there is nothing to plot. The ratings are in the risk register.
            </Empty>
          ) : (
          <ResponsiveContainer width="100%" height={300}>
            <ScatterChart margin={{ top: 12, right: 24, bottom: 24, left: 8 }}>
              <CartesianGrid stroke={T.line} />
              <XAxis type="number" dataKey="likelihood" domain={[0, 5.5]} ticks={[1, 2, 3, 4, 5]}
                {...axis} label={{ value: "Likelihood", position: "insideBottom", offset: -12,
                  fontSize: 11, fill: T.soft }} />
              <YAxis type="number" dataKey="impact" domain={[0, 5.5]} ticks={[1, 2, 3, 4, 5]}
                {...axis} label={{ value: "Impact", angle: -90, position: "insideLeft",
                  fontSize: 11, fill: T.soft }} />
              <ZAxis type="number" dataKey="count" range={[120, 900]} />
              <Tooltip {...tooltipStyle()} cursor={{ strokeDasharray: "3 3" }}
                content={({ payload }) => {
                  if (!payload?.length) return null;
                  const c = payload[0].payload;
                  return (
                    <div style={{ background: "#fff", border: `1px solid ${T.line}`,
                                  borderRadius: 4, padding: 10, fontSize: 12.5, maxWidth: 280 }}>
                      <b style={{ color: T.navy }}>
                        {c.count} risk{c.count === 1 ? "" : "s"} · score {c.score}
                      </b>
                      <div style={{ color: T.soft, marginTop: 2 }}>
                        Likelihood {c.likelihood}, impact {c.impact}
                      </div>
                      <div style={{ marginTop: 6, color: T.ink }}>
                        {c.titles.join(", ")}{c.count > c.titles.length ? "…" : ""}
                      </div>
                    </div>
                  );
                }} />
              <Scatter data={model.matrix} cursor="pointer"
                onClick={(d) => { const dat = datum(d);
                  open("risks",
                    `Open risks scoring ${dat.score} (likelihood ${dat.likelihood}, impact ${dat.impact})`,
                    dat.rows); }}>
                {model.matrix.map((c, i) => (
                  <Cell key={i}
                    fill={c.score >= model.threshold ? SERIES.overdue : SERIES.inProgress}
                    fillOpacity={0.85} stroke="#fff" strokeWidth={2} />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
          )}
        </ChartPanel>
      )}

      {model.permits.length > 0 && (
        <ChartPanel
          title="Permits by category"
          table={<SimpleTable head={["Category", "Open", "Closed"]}
            onRow={(i) => open("permits", `${model.permits[i].category}: all`,
              [...model.permits[i].rows.Open, ...model.permits[i].rows.Closed])}
            rows={model.permits.map((p) => [p.category, p.Open, p.Closed])} />}
        >
          <ResponsiveContainer width="100%" height={Math.max(220, model.permits.length * 34)}>
            <BarChart layout="vertical" data={model.permits} margin={{ left: 8, right: 24 }}>
              <CartesianGrid {...gridProps} vertical horizontal={false} />
              <XAxis type="number" allowDecimals={false} {...axis} />
              <YAxis type="category" dataKey="category" width={200} {...axis} />
              <Tooltip {...tooltipStyle()} />
              <Legend wrapperStyle={{ fontSize: 12, color: T.soft }} />
              <Bar dataKey="Open" stackId="p" fill={SERIES.inProgress} cursor="pointer"
                onClick={(d) => { const dat = datum(d);
                  open("permits", `${dat.category}: open`, dat.rows?.Open); }} />
              <Bar dataKey="Closed" stackId="p" fill={SERIES.complete} radius={[0, 4, 4, 0]}
                cursor="pointer"
                onClick={(d) => { const dat = datum(d);
                  open("permits", `${dat.category}: closed`, dat.rows?.Closed); }} />
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>
      )}

      {model.invoices.open.length > 0 && (
        <ChartPanel
          title="Open invoice dollars by aging"
          accent={T.gold}
          note="Measured against each invoice's own payment terms, not calendar age. An invoice with no terms of its own uses its vendor's, then the contract's."
          table={<SimpleTable head={["Bucket", "Dollars", "Invoices"]}
            onRow={(i) => open("invoices", `Invoices ${model.aging[i].bucket.toLowerCase()}`,
              model.aging[i].rows)}
            rows={model.aging.map((a) => [a.bucket, fmtMoney(a.dollars), a.count])} />}
        >
          <ResponsiveContainer width="100%" height={240}>
            {/* The handler is on the chart rather than on the bar. This is the
                one chart here whose bars are drawn as Cells so each bucket can
                take its own colour, and a Cell does not inherit the bar's
                click. On the chart it also means the whole column is a target,
                including the empty space above a short bar. There is one
                series, so the column and the bucket are the same thing. */}
            <BarChart data={model.aging} margin={{ left: 8, right: 16 }}
              onClick={(state) => {
                const a = state?.activePayload?.[0]?.payload;
                if (a) open("invoices", `Invoices ${String(a.bucket).toLowerCase()}`, a.rows);
              }}>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="bucket" {...axis} />
              <YAxis tickFormatter={fmtK} {...axis} />
              <Tooltip {...tooltipStyle()} formatter={(v) => fmtMoney(v)} />
              <Bar dataKey="dollars" radius={[4, 4, 0, 0]} cursor="pointer">
                {model.aging.map((a, i) => (
                  <Cell key={i} fill={i === 0 ? SERIES.notStarted
                    : i < 3 ? SERIES.inProgress : SERIES.overdue} />
                ))}
                <LabelList dataKey="dollars" position="top"
                  formatter={(v) => (v ? fmtK(v) : "")}
                  style={{ fill: T.soft, fontSize: 11, pointerEvents: "none" }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>
      )}
    </div>
  );
}
